import {
    useSearch,
    useSelect,
    useSortedData,
} from '@/features/analytics/audience/hooks'
import { NiceChannelBox } from '@/features/analytics/components/nice-channel-box'
import { useInviteLinksStore } from '@/features/analytics/model'
import { AudienceTableRowType } from '@/features/analytics/model/slices/audienceSlice'
import { MagnifyingGlass, PremiumImage } from '@/shared/assets'
import { SortHeader, StaticHeader, Table, UserIcon } from '@/shared/components'
import { CopyLink } from '@/shared/ui'
import { parseTelegramPeerReference } from '@synapex/shared2/telegram'
import clsx from 'clsx'
import { useRef } from 'react'
import './audienceTable.scss'
import { Channel } from '@/true-fsd/data/advertiser-account'

interface AudienceTableProps {
    data: AudienceTableRowType[]
    activeChannel: Channel
}

export const AudienceTable = ({ data, activeChannel }: AudienceTableProps) => {
    const {
        // audienceByChannel,
        startDateAudience,
        endDateAudience,
        trafficSourceSortAudience,
        usersSortAudience,
        subscriberStatusSortAudience,
        lineDateAudience,
    } = useInviteLinksStore()
    // const data = audienceByChannel[activeWorkspace?.channel_id ?? 0] || []
    const { searchQuery, setSearchQuery, filteredData } = useSearch(data)
    const { selectedData } = useSelect({
        data: filteredData,
        startDate: startDateAudience,
        endDate: endDateAudience,
        lineDate: lineDateAudience,
        trafficSource: trafficSourceSortAudience,
        usersSort: usersSortAudience,
        subscriberStatus: subscriberStatusSortAudience,
        channelCreatedAt: activeChannel.created_at,
    })
    const { sortedData, handleSort, sortConfig } = useSortedData(selectedData)

    const tableContainerRef = useRef<HTMLDivElement>(null)

    const tableHead = (
        <tr>
            <th>
                <StaticHeader title="№" />
            </th>
            <th>
                <div className="search-header">
                    <div
                        className={clsx('search-input-wrapper', {
                            'has-text': searchQuery,
                        })}
                    >
                        <div className="search-icon">
                            <img src={MagnifyingGlass} alt="Search" />
                        </div>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Имя"
                            className="search-input"
                        />
                    </div>
                </div>
            </th>
            <th>
                <SortHeader<AudienceTableRowType>
                    title="Никнейм"
                    field="username"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AudienceTableRowType>
                    title="Источник"
                    field="source"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <StaticHeader
                    title="Ссылка"
                    spanClassName="static-header__left"
                />
            </th>
            <th>
                <StaticHeader
                    title="Telegram ID"
                    spanClassName="static-header__left"
                />
            </th>
            <th>
                <SortHeader<AudienceTableRowType>
                    title="Дата подписки"
                    field="subscriptionDate"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AudienceTableRowType>
                    title="Дата отписки"
                    field="unsubscriptionDate"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<AudienceTableRowType>
                    title="Время в канале"
                    field="timeInChannel"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
        </tr>
    )

    const tableBody = (
        <>
            {sortedData.map((row, index) => {
                const sourceRef = row.source
                    ? parseTelegramPeerReference(row.source)
                    : null

                return (
                    <tr key={row.id}>
                        <td>{index + 1}</td>
                        <td>
                            <div className="audience-table__user-cell">
                                <UserIcon
                                    username={row.username ?? '@'}
                                    photoUrl={row.userPhotoUrl}
                                />
                                <div className="text">
                                    {row.name === '@null' ||
                                    row.name === 'null' ||
                                    !row.name ? (
                                        <hr className="gray-long-dash" />
                                    ) : (
                                        row.name
                                    )}
                                </div>
                                {row.isPremium && (
                                    <img
                                        src={PremiumImage}
                                        alt="Premium Badge"
                                        className="premium-icon"
                                    />
                                )}
                            </div>
                        </td>
                        <td>
                            {row.username == null ? (
                                <hr className="gray-long-dash" />
                            ) : (
                                <CopyLink text={`@${row.username}`} />
                            )}
                        </td>
                        <td>
                            <div>
                                {sourceRef ? (
                                    <NiceChannelBox
                                        {...sourceRef}
                                        title={row.title}
                                        channelId={activeChannel.channel_id}
                                        className={clsx(
                                            'name-cell__channel-box'
                                        )}
                                        photoUrl={row.sourcePhotoUrl}
                                        // rknStatus={row.rknStatus}
                                    />
                                ) : row.source ? (
                                    <div className="text">{row.source}</div>
                                ) : (
                                    <hr className="gray-long-dash" />
                                )}
                            </div>
                        </td>
                        <td>
                            {row.link ? (
                                <CopyLink text={row.link} />
                            ) : (
                                <hr className="gray-long-dash" />
                            )}
                        </td>
                        <td>{row.telegramId}</td>
                        <td>
                            {row.subscriptionDate ? (
                                `${new Date(row.subscriptionDate).toLocaleDateString('ru-RU')} ${new Date(
                                    row.subscriptionDate
                                ).toLocaleTimeString('ru-RU', {
                                    hour: 'numeric',
                                    minute: 'numeric',
                                })}`
                            ) : (
                                <hr className="gray-long-dash" />
                            )}
                        </td>
                        <td>
                            {row.unsubscriptionDate ? (
                                `${new Date(row.unsubscriptionDate).toLocaleDateString('ru-RU')} ${new Date(
                                    row.unsubscriptionDate
                                ).toLocaleTimeString('ru-RU', {
                                    hour: 'numeric',
                                    minute: 'numeric',
                                })}`
                            ) : (
                                <hr className="gray-long-dash" />
                            )}
                        </td>
                        <td>{row.timeInChannel}</td>
                    </tr>
                )
            })}
        </>
    )

    return (
        <Table
            isLoading={false}
            tableContainerRef={tableContainerRef}
            thead={tableHead}
            tbody={tableBody}
            className="audience-table"
            baseTableClassName="audience-base-table"
        />
    )
}
